import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FlagOutlined as KraIcon,
  CheckCircleOutline as KpiIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const cycleColors = {
  Q1: 'primary',
  Q2: 'secondary',
  Q3: 'warning',
  Q4: 'error',
  Annual: 'success',
};

const statusColors = {
  draft: 'default',
  submitted: 'warning',
  approved: 'success',
  rejected: 'error',
};

const ManagerKpiProposals = () => {
  const { getAuthHeaders } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/performance/proposals/team`, {
        headers: getAuthHeaders(),
      });
      setProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Error fetching team KPI proposals:', error);
      toast.error('Failed to fetch team KPI/KRA proposals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  const openDialog = (proposal) => {
    setSelectedProposal(proposal);
    setShowRejectForm(false);
    setRejectReason('');
  };

  const closeDialog = () => {
    setSelectedProposal(null);
    setShowRejectForm(false);
    setRejectReason('');
  };

  const handleApprove = async () => {
    try {
      setReviewing(true);
      await axios.post(
        `${API_URL}/performance/proposals/${selectedProposal._id}/review`,
        { decision: 'approved' },
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal approved');
      closeDialog();
      fetchProposals();
    } catch (error) {
      console.error('Error approving proposal:', error);
      toast.error(error.response?.data?.message || 'Failed to approve proposal');
    } finally {
      setReviewing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejecting this proposal');
      return;
    }
    try {
      setReviewing(true);
      await axios.post(
        `${API_URL}/performance/proposals/${selectedProposal._id}/review`,
        { decision: 'rejected', reviewComments: rejectReason },
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal rejected');
      closeDialog();
      fetchProposals();
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast.error(error.response?.data?.message || 'Failed to reject proposal');
    } finally {
      setReviewing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>KPI/KRA Proposals - Team</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and approve the KPIs/KRAs your team members have proposed for upcoming review cycles.
      </Typography>

      {proposals.length === 0 ? (
        <Alert severity="info">No KPI/KRA proposals from your team yet.</Alert>
      ) : (
        <Grid container spacing={3}>
          {proposals.map((proposal) => {
            return (
              <Grid item xs={12} md={6} key={proposal._id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {proposal.employeeId?.firstName} {proposal.employeeId?.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {proposal.employeeId?.email}
                    </Typography>
                    <Box mt={1} mb={2} display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={`${proposal.reviewCycle} ${proposal.reviewYear}`}
                        color={cycleColors[proposal.reviewCycle] || 'default'}
                        size="small"
                      />
                      <Chip
                        label={proposal.status}
                        color={statusColors[proposal.status] || 'default'}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                    <Button variant="contained" onClick={() => openDialog(proposal)}>
                      Review
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={!!selectedProposal} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          KPI/KRA Proposal — {selectedProposal?.employeeId?.firstName} {selectedProposal?.employeeId?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          {selectedProposal && (
            <>
              <Box display="flex" gap={1} mb={2}>
                <Chip
                  label={`${selectedProposal.reviewCycle} ${selectedProposal.reviewYear}`}
                  color={cycleColors[selectedProposal.reviewCycle] || 'default'}
                  size="small"
                />
                <Chip
                  label={selectedProposal.status}
                  color={statusColors[selectedProposal.status] || 'default'}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              {(selectedProposal.kras || []).length > 0 ? (
                selectedProposal.kras.map((kra, kraIdx) => (
                  <Accordion key={kraIdx} disableGutters elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                        <KraIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                          {kra.name}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {kra.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {kra.description}
                        </Typography>
                      )}
                      <Stack spacing={1}>
                        {(kra.kpis || []).map((kpi, kpiIdx) => (
                          <Box key={kpiIdx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <KpiIcon sx={{ color: 'success.main', mt: 0.2 }} fontSize="small" />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{kpi.name}</Typography>
                              {kpi.description && (
                                <Typography variant="body2" color="text.secondary">{kpi.description}</Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">No KRAs in this proposal.</Typography>
              )}

              {selectedProposal.status === 'submitted' && (
                <Box mt={3}>
                  {!showRejectForm ? (
                    <Box display="flex" gap={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowRejectForm(true)}
                        disabled={reviewing}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApprove}
                        disabled={reviewing}
                      >
                        {reviewing ? <CircularProgress size={24} /> : 'Approve'}
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle1" gutterBottom color="error">
                        Reject Proposal
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason for rejection *"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Explain what should be revised..."
                        required
                      />
                      <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                        <Button variant="outlined" onClick={() => setShowRejectForm(false)} disabled={reviewing}>
                          Cancel
                        </Button>
                        <Button variant="contained" color="error" onClick={handleReject} disabled={reviewing}>
                          {reviewing ? <CircularProgress size={24} /> : 'Confirm Rejection'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagerKpiProposals;
